import React, { useEffect, useMemo, useRef, useState, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import Liveticker from '@fec/frontend/modules/liveticker/Liveticker/Liveticker.jsx';
import { TickerItem } from './TickerItem.jsx';
import {
  addNewMessage,
  addNewMessages,
  generateSchemaOrgData,
  generateStatusText,
  getItemIdFromAnchor,
  getItemToJumpTo,
  getNrOfNewerMessages,
  getNrOfOlderMessages,
  getTickerStatus,
  handleLinkClick,
  remakeShownMessages,
  removeMessage,
  scrollToElement,
} from './TickerHelperFunctions.js';
import { JUMP_TO_ANCHOR } from '@fec/assets/js/utils/event.js';
import ClientStorage from '@fec/frontend/foundation/client/storage.js';
import { getConfig } from '@fec/assets/js/utils/config.js';
import * as Ably from 'ably';
export const SHOW_INITIAL = 5;
const SHOW_ADDITIONAL = 10;

const initialState = {
  messages: [],
  shownMessages: [],
  highlightedId: null,
  focussedId: null,
  initialLoadComplete: false,
};

const messageReducer = (state, action) => {
  switch (action.type) {
    case 'BACKEND_LOAD': {
      const messages = addNewMessages(state.messages, action.payload);

      // make sure we don't miss an update by fetching itemToJumpTo again
      const itemToBeHighlighted = state.highlightedId ?? getItemToJumpTo();

      if (itemToBeHighlighted) {
        const highlightIndex = messages.findIndex(
          ({ id }) => String(id) === String(itemToBeHighlighted),
        );

        if (highlightIndex >= 0) {
          // item which should be highlighted exists! Show it and the following 4 items
          const shownMessages = messages.slice(
            highlightIndex,
            highlightIndex + SHOW_INITIAL,
          );

          return {
            ...state,
            shownMessages,
            messages,
            highlightedId: itemToBeHighlighted,
            focussedId: shownMessages[0].id,
            initialLoadComplete: true,
          };
        }
      }

      const shownMessages = messages.slice(0, SHOW_INITIAL);
      return {
        ...state,
        shownMessages,
        messages,
        highlightedId: initialState.highlightedId, // in case there was a highlightedId, but it didn't exist in the messages
        initialLoadComplete: true,
      };
    }
    case 'SHOW_NEWEST': {
      // jump to the top: re-create shownMessages from messages, starting at
      // the top until the end of the previously last shown messages
      const bottomItemId =
        state.shownMessages[state.shownMessages.length - 1]?.id;
      const bottomItemIndex = state.messages.findIndex(
        ({ id }) => id === bottomItemId,
      );
      const shownMessages = state.messages.slice(0, bottomItemIndex + 1);

      return {
        ...state,
        highlightedId: initialState.highlightedId,
        focussedId: shownMessages[0].id,
        shownMessages,
      };
    }
    case 'SHOW_EARLIER': {
      // show earlier messages: re-create shownMessages from messages, starting
      // from the previously first shown message to the previously last shown
      // message + 10 more
      const idOfTopShownMessage = state.shownMessages[0].id;

      const indexOfTopShownIndexInMessages = state.messages.findIndex(
        ({ id }) => id === idOfTopShownMessage,
      );

      // focus on first message that is newly shown
      const idToBeFocussed =
        state.messages[
          indexOfTopShownIndexInMessages + state.shownMessages.length
        ].id;

      const shownMessages = state.messages.slice(
        indexOfTopShownIndexInMessages,
        indexOfTopShownIndexInMessages +
          state.shownMessages.length +
          SHOW_ADDITIONAL,
      );

      return {
        ...state,
        focussedId: idToBeFocussed,
        shownMessages,
      };
    }
    case 'ANCHOR_CHANGE': {
      const highlightedId = action.payload;

      // an anchor change can also happen before the messages are fetched
      if (state.messages.length > 0) {
        const highlightIndex = state.messages.findIndex(
          ({ id }) => String(id) === String(highlightedId),
        );

        if (highlightIndex >= 0) {
          // item which should be highlighted exists! Show it and the following 4 items
          const shownMessages = state.messages.slice(
            highlightIndex,
            highlightIndex + SHOW_INITIAL,
          );

          // focus on first item
          const focussedId = highlightedId;
          return {
            ...state,
            highlightedId,
            focussedId,
            shownMessages,
          };
        } else {
          // item-to-be-highlighted does not exist - show the first 5
          const shownMessages = state.messages.slice(0, SHOW_INITIAL);

          // focus on first item
          const focussedId = shownMessages[0].id;
          return {
            ...state,
            focussedId,
            shownMessages,
          };
        }
      } else {
        // no messages yet, so we just save the id of the item-to-be-highlighted
        return { ...state, highlightedId };
      }
    }
    case 'ABLY_REMOVE': {
      const idOfMessageToBeRemoved = String(action.payload);
      const messages = removeMessage(state.messages, idOfMessageToBeRemoved);

      // update shownMessages if the removed message was shown
      const removedMessageWasShown = !!state.shownMessages.find(
        ({ id }) => String(id) === idOfMessageToBeRemoved,
      );

      return {
        ...state,
        messages,
        shownMessages: removedMessageWasShown
          ? remakeShownMessages(messages, state.shownMessages)
          : state.shownMessages,
      };
    }
    case 'ABLY_UPDATE': {
      if (action?.payload?.data?.publishedAt) {
        action.payload.data.timestamp = Math.floor(
          Date.parse(action.payload.data.publishedAt) / 1000,
        );
      }
      const newMessageId = action?.payload?.data?.id;
      const messages = addNewMessage(state.messages, action.payload);

      let needToRemakeShownMessages = false;
      const isNewMessage = !state.messages.find(
        ({ id }) => String(id) === newMessageId,
      );

      // update shownMessages if it was a new message or an edit to a shown Message
      if (isNewMessage) {
        needToRemakeShownMessages = true;
      } else {
        const wasShown = !!state.shownMessages.find(
          ({ id }) => String(id) === newMessageId,
        );

        if (wasShown) {
          needToRemakeShownMessages = true;
        }
      }

      return {
        ...state,
        messages,
        shownMessages: needToRemakeShownMessages
          ? remakeShownMessages(messages, state.shownMessages)
          : state.shownMessages,
      };
    }
    case 'AFTER_SCROLL': {
      // after scrolling, we reset the highlightedId, so we don't scroll again
      return {
        ...state,
        highlightedId: initialState.highlightedId,
      };
    }
    default:
      throw new Error();
  }
};

export const TICKER_STATUS = {
  LIVE: 'live',
  SCHEDULED: 'scheduled',
  CLOSED: 'closed',
};

export const Ticker = ({
  accessToken,
  channelName,
  apiEndpoint,
  active,
  startTime,
  label,
}) => {
  const ref = useRef();
  const [loading, setLoading] = useState(true);
  const [state, dispatch] = useReducer(messageReducer, initialState);
  const { t } = useTranslation();
  const status = getTickerStatus(active, startTime);

  const config = getConfig();
  const representation = config.REPRESENTATION ?? 'web';
  const isWebview = representation.includes('webview');

  useEffect(() => {
    fetch(apiEndpoint)
      // eslint-disable-next-line no-console
      .catch((err) => console.error(err))
      .then((response) => response.json())
      .then((content) =>
        dispatch({ type: 'BACKEND_LOAD', payload: content.items }),
      )
      .then(() => {
        if (status === TICKER_STATUS.LIVE) {
          const client = new Ably.Realtime(accessToken);
          const channel = client.channels.get(`[?rewind=120s]${channelName}`);

          channel.attach(() =>
            channel.subscribe((msg) => {
              switch (msg.name) {
                case 'purge':
                case 'retire':
                  dispatch({ type: 'ABLY_REMOVE', payload: msg.data.id });
                  break;
                default:
                  dispatch({ type: 'ABLY_UPDATE', payload: msg });
              }
            }),
          );
        }
      })
      .finally(() => setLoading(false));
  }, [status, channelName]);

  // handle bridge event
  useEffect(() => {
    const handleAnchorChange = (data) => {
      // only handle the anchor change when the initial loading is already complete, otherwise we're too early
      if (state.initialLoadComplete) {
        const itemId = getItemIdFromAnchor(data?.detail?.anchor);
        dispatch({ type: 'ANCHOR_CHANGE', payload: itemId });
      }
    };

    window.addEventListener(JUMP_TO_ANCHOR, handleAnchorChange);
    return () => window.removeEventListener(JUMP_TO_ANCHOR, handleAnchorChange);
  }, [ref.current, state.initialLoadComplete]);

  // write schema.org data to script in head, so it can be parsed
  useEffect(
    () => generateSchemaOrgData(startTime, state.messages),
    [state.messages, startTime],
  );

  // scrolling!
  useEffect(() => {
    if (ref.current && state.initialLoadComplete && state.highlightedId) {
      dispatch({ type: 'AFTER_SCROLL' });
      ClientStorage.removeItem('srf:anchor');
      scrollToElement(ref.current);
    }
  }, [ref.current, state.initialLoadComplete, state.highlightedId]);

  // clicking on links from an article to a liveticker item inside the same
  // article should be prevented and handled internally, not via browser.
  useEffect(() => {
    const handleAnyLinkClick = (e) =>
      handleLinkClick(e, state.messages, (anchor) =>
        dispatch({ type: 'ANCHOR_CHANGE', payload: anchor }),
      );

    document.body.addEventListener('click', handleAnyLinkClick);
    return () => {
      document.body.removeEventListener('click', handleAnyLinkClick);
    };
  }, [state.messages, ref.current]);

  const nrOfNewerMessages = useMemo(
    () => getNrOfNewerMessages(state.shownMessages, state.messages),
    [state.messages, state.shownMessages],
  );

  const nrOfEarlierMessages = useMemo(
    () => getNrOfOlderMessages(state.shownMessages, state.messages),
    [state.messages, state.shownMessages],
  );

  const showCurrentMessages = () => {
    dispatch({ type: 'SHOW_NEWEST' });
    scrollToElement(ref.current);
  };

  const showEarlierMessages = () => dispatch({ type: 'SHOW_EARLIER' });

  return (
    <Liveticker
      ref={ref}
      status={status}
      statusText={generateStatusText(label, startTime, t)[status]}
      showSpinner={loading}
      onShowCurrent={showCurrentMessages}
      onShowEarlier={showEarlierMessages}
      earlierMessages={nrOfEarlierMessages}
      laterMessages={nrOfNewerMessages}>
      {state.shownMessages.map((item) => (
        <TickerItem
          key={item.data.id}
          title={item.data.title}
          date={item.data.publishedAt}
          slateJson={item.data.text}
          isFocussed={item.data.id === state.focussedId}
          isWebview={isWebview}
        />
      ))}
    </Liveticker>
  );
};
